import type { FC } from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { OktaAuth } from '@okta/okta-auth-js';
import { doSetOktaAccessTokenCookie, useCookieListener } from '../lib/utils';
import { BusyIndicator } from '@fctg-corporate/smartscript-core';

type Props = {
	oktaAuth: OktaAuth;
	tokenName: string;
	path: string
};

const Callback: FC<Props> = ({ oktaAuth, tokenName, path }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const cookieIsSet = useCookieListener(tokenName);

	useEffect(() => {
		(async () => {
			if (oktaAuth.isLoginRedirect()) {
				const { tokens } = await oktaAuth.token.parseFromUrl();

				if (tokens.accessToken) {
					oktaAuth.tokenManager.add(tokenName, tokens.accessToken);

					if (!cookieIsSet) {
						await doSetOktaAccessTokenCookie(oktaAuth, tokenName);
					}
				}

				if (tokens.idToken) {
					oktaAuth.tokenManager.add('idToken', tokens.idToken);
				}

				navigate(location.pathname, { replace: true });
			}

			const queueJson = localStorage.getItem('smartscriptLoginQueue');

			if (queueJson) {
				const queue: string[] = JSON.parse(queueJson);
				const currentPath = path;
				const currentIndex = queue.indexOf(currentPath);
				const nextPath = queue[currentIndex + 1];

				if (nextPath) {
					navigate(nextPath);

					return;
				}

				localStorage.removeItem('smartscriptLoginQueue');
			}

			navigate('/', { replace: true });
		})();
	}, [oktaAuth, tokenName, location.pathname, navigate, cookieIsSet, path]);

	return <BusyIndicator label={`Setting token ${tokenName} for ${path}...`}/>;
};

export default Callback;
